import { API_HEADERS, createDesignationsLookupHeaders } from '../constants/apiHeaders';
import api from '../utils/api';
import { SYSTEM_ENDPOINTS } from './endpoints/system.endpoints';
import { VACANCY_ENDPOINTS } from './endpoints/rec/vacancy.endpoints';
import type {
  JiraStatus,
  VacancySearchParams,
  VacancySearchResponse,
  VacancyStage,
  VacancyStageQuestion,
  VacancyStageQuestionRequest,
  VacancyStageRequest,
} from './types/vacancy.types';

export const vacancyService = {
  search: (
    params: VacancySearchParams,
    extraHeaders?: Record<string, string>,
  ) => {
    const queryParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== undefined) {
        queryParams[key] = String(value);
      }
    });
    return api.get<VacancySearchResponse>(
      `${VACANCY_ENDPOINTS.search}?${new URLSearchParams(queryParams)}`,
      {
        headers: {
          ...API_HEADERS.VACANCY_SEARCH_LIST,
          ...extraHeaders,
        },
      },
    );
  },

  delete: (vacancyId: number) =>
    api.delete(VACANCY_ENDPOINTS.byId(vacancyId), {
      headers: { ...API_HEADERS.VACANCY_SEARCH_DELETE },
    }),

  clone: (vacancyId: number) =>
    api.post(VACANCY_ENDPOINTS.clone(vacancyId), {}, {
      headers: { ...API_HEADERS.VACANCY_SEARCH_CLONE },
    }),

  publishJob: (vacancyId: number) =>
    api.post(VACANCY_ENDPOINTS.publishJob(vacancyId), {}, {
      headers: { ...API_HEADERS.VACANCY_SEARCH_PUBLISH_JOB },
    }),

  deleteJob: (vacancyId: number) =>
    api.post(VACANCY_ENDPOINTS.deleteJob(vacancyId), {}, {
      headers: { ...API_HEADERS.VACANCY_SEARCH_DELETE_JOB },
    }),

  withdrawApproval: (vacancyId: number) =>
    api.put(VACANCY_ENDPOINTS.withdraw(vacancyId), {}, {
      headers: { ...API_HEADERS.VACANCY_SEARCH_WITHDRAW },
    }),

  getList: () =>
    api.get<{
      vacancies: {
        id: number;
        vacancyName: string;
        vacancyCode: string;
        vacancyType: string;
      }[];
    }>(VACANCY_ENDPOINTS.list),

  searchDesignations: (query: string, pageId: number) =>
    api.get<{ data: { id: number; designation: string; designationCode: string }[] }>(
      SYSTEM_ENDPOINTS.designations,
      {
        headers: { ...createDesignationsLookupHeaders(pageId) },
      },
    ),

  /** Active Jira statuses a stage can be mapped to. */
  getJiraStatuses: () =>
    api.get<{ data: JiraStatus[] }>(VACANCY_ENDPOINTS.jiraStatuses, {
      headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_LIST },
    }),

  /** Stages (GENERAL first, then INTERVIEW in pipeline order) of a vacancy. */
  getVacancyStages: (vacancyId: number) =>
    api.get<{ data: VacancyStage[] }>(VACANCY_ENDPOINTS.stages(vacancyId), {
      headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_LIST },
    }),

  /** Creating the GENERAL stage also copies the active master general questions into it. */
  createVacancyStage: (vacancyId: number, payload: VacancyStageRequest) =>
    api.post<{ message: string; data: VacancyStage }>(VACANCY_ENDPOINTS.stages(vacancyId), payload, {
      headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_SAVE },
    }),

  updateVacancyStage: (vacancyId: number, stageId: number, payload: VacancyStageRequest) =>
    api.put<{ message: string; data: VacancyStage }>(
      VACANCY_ENDPOINTS.stage(vacancyId, stageId),
      payload,
      { headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_SAVE } },
    ),

  /** 409 when the stage is allocated to applicants or the GENERAL stage still has interview stages. */
  deleteVacancyStage: (vacancyId: number, stageId: number) =>
    api.delete<{ message: string }>(VACANCY_ENDPOINTS.stage(vacancyId, stageId), {
      headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_DELETE },
    }),

  /** `stageIds` lists every stage of the vacancy in the new order; the GENERAL stage stays first. */
  reorderVacancyStages: (vacancyId: number, stageIds: number[]) =>
    api.put<{ message: string; data: VacancyStage[] }>(
      VACANCY_ENDPOINTS.reorderStages(vacancyId),
      { stageIds },
      { headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_SAVE } },
    ),

  getVacancyStageQuestions: (vacancyId: number, stageId: number) =>
    api.get<{ data: VacancyStageQuestion[] }>(VACANCY_ENDPOINTS.stageQuestions(vacancyId, stageId), {
      headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_LIST },
    }),

  /** Saves the stage questions in display order (position = sortOrder). */
  saveVacancyStageQuestions: (
    vacancyId: number,
    stageId: number,
    questions: VacancyStageQuestionRequest[],
  ) =>
    api.put<{ message: string; data: VacancyStageQuestion[] }>(
      VACANCY_ENDPOINTS.stageQuestions(vacancyId, stageId),
      { questions },
      { headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_SAVE } },
    ),
};
